import FilterSelect from '@/components/inputs/FilterSelect';
import Input from '@/components/inputs/Input';
import PortalInputController from '@/components/inputs/PortalInputController';
import { Fragment, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { Option, walletTypeOptions, WalletTypes } from '@/constants/general';

import PortalEntry from '@/components/common/PortalEntry';
import { PortalWhitelistRequestFormValues } from '@/components/form/schema/portalWhitelistSchema';
import MultipleSelect from '@/components/inputs/MultiSelect';
import { PortalInputCheckbox } from '@/components/inputs/PortalInputCheckbox/PortalInputCheckbox';
import TextArea from '@/components/inputs/TextArea';
import { getCurrency } from '@/utils/hooks/useCurrency';
import useLanguage from '@/utils/hooks/useLanguage';
import { useSso } from '@/utils/providers/SSOProvider';

interface RequestWalletInputsProps {}

const RequestWalletInputs = {
    Section1: (props: RequestWalletInputsProps) => {
        const lang = useLanguage();
        const { blockchainsApi, vaspApi } = useSso();
        const {
            setValue,
            trigger,
            watch,
            formState: { errors }
        } = useFormContext<PortalWhitelistRequestFormValues>();

        const [networkId, walletType, vaspId] = watch(['networkId', 'type', 'vaspId']);

        const { data: blockchainsData, isLoading: isLoadingBlockchainsData } = blockchainsApi;
        const { data: vaspData, isLoading: isLoadingVaspData } = vaspApi;

        const assetOptions = useMemo(() => {
            return blockchainsData
                ?.find((network) => network.id === networkId?.value)
                ?.assets.map((asset) => {
                    const { show } = getCurrency(asset.digitalAssetName);
                    return {
                        label: show || asset.digitalAssetName || asset.currencyName,
                        value: asset.currencyId
                    };
                });
        }, [blockchainsData, networkId]);

        const vaspOptions: Option[] = useMemo(() => {
            return vaspData?.filter((vasp) => vasp.active).map((vasp) => ({ label: vasp.name, value: vasp.id })) || [];
        }, [vaspData, isLoadingVaspData]);

        const vaspDetails = useMemo(() => {
            if (!vaspId?.value) return null;
            return vaspData?.find((vasp) => vasp.id === vaspId?.value);
        }, [vaspId]);

        const networkOptions = useMemo(() => {
            return (
                blockchainsData &&
                blockchainsData?.map((network) => {
                    return {
                        label: network.name,
                        value: network.id
                    };
                })
            );
        }, [blockchainsData]);

        useEffect(() => {
            if (assetOptions && assetOptions.length < 2) {
                setValue('assetsId', assetOptions);
                trigger('assetsId');
            }
        }, [assetOptions]);

        return (
            <Fragment>
                <fieldset className="space-y-3">
                    <legend className="text-xs text-brand-neutral-500">{lang.portal.walletInformation}</legend>
                    <div className="flex flex-col xsm:flex-row gap-4 w-full">
                        <div className="flex-1">
                            <PortalInputController
                                name="friendlyName"
                                label={lang.portal.friendlyName}
                                placeholder={lang.portal.friendlyName}
                                required>
                                <Input />
                            </PortalInputController>
                        </div>
                        <div className="flex-1">
                            <PortalInputController label={lang.portal.walletType} name="type" required>
                                <FilterSelect options={walletTypeOptions || []} enableSorting />
                            </PortalInputController>
                        </div>
                    </div>
                </fieldset>

                {walletType?.value === WalletTypes.Regulated && (
                    <fieldset className="space-y-3 mt-3">
                        <legend className="text-xs text-brand-neutral-500">{lang.portal.vaspInformation}</legend>
                        <div className="flex flex-col xsm:flex-row gap-4 w-full">
                            <div className="flex-1">
                                <PortalInputController
                                    name="vaspId"
                                    label={lang.portal.vasp}
                                    required={walletType?.value === WalletTypes.Regulated}>
                                    <FilterSelect options={vaspOptions || []} />
                                </PortalInputController>
                            </div>
                            {vaspDetails?.legalEntityName && (
                                <div className="flex-1">
                                    <PortalEntry
                                        label={lang.portal.vaspLEN}
                                        value={vaspDetails?.legalEntityName}
                                        hidden={!vaspDetails?.legalEntityName}
                                        disabled
                                    />
                                </div>
                            )}
                        </div>
                        <div className="flex flex-col xsm:flex-row gap-4 w-full">
                            {vaspDetails?.country && (
                                <div className="flex-1">
                                    <PortalEntry
                                        label={lang.portal.vaspCountry}
                                        value={vaspDetails?.country || '-'}
                                        hidden={!vaspDetails?.country}
                                        disabled
                                    />
                                </div>
                            )}
                            {vaspDetails?.lei && (
                                <div className="flex-1">
                                    <PortalEntry
                                        label={lang.portal.vaspLEI}
                                        value={vaspDetails?.lei || '-'}
                                        hidden={!vaspDetails?.lei}
                                        disabled
                                    />
                                </div>
                            )}
                        </div>
                        <div className="flex flex-col xsm:flex-row gap-4 w-full">
                            {vaspDetails?.emailAddress && (
                                <div className="flex-1">
                                    <PortalEntry
                                        label={lang.portal.vaspEmail}
                                        value={vaspDetails?.emailAddress || '-'}
                                        hidden={!vaspDetails?.emailAddress}
                                        disabled
                                    />
                                </div>
                            )}
                            {vaspDetails?.address && (
                                <div className="flex-1">
                                    <PortalEntry
                                        label={lang.portal.vaspAddress}
                                        value={vaspDetails?.address || '-'}
                                        hidden={!vaspDetails?.address}
                                        disabled
                                    />
                                </div>
                            )}
                        </div>
                    </fieldset>
                )}

                <fieldset className="space-y-3 mt-3">
                    <legend className="text-xs text-brand-neutral-500">{lang.portal.networkInformation}</legend>
                    <div className="flex flex-col xsm:flex-row gap-4 w-full">
                        <div className="flex-1">
                            <PortalInputController
                                name="networkId"
                                label={lang.portal.network}
                                placeholder={lang.portal.network}
                                asyncInput={isLoadingBlockchainsData}>
                                <FilterSelect options={networkOptions || []} enableSorting />
                            </PortalInputController>
                        </div>
                        <div className="flex-1">
                            <PortalInputController
                                name="assetsId"
                                label={lang.portal.assets}
                                placeholder={lang.portal.assets}
                                disabled={assetOptions && assetOptions.length < 2}>
                                <MultipleSelect options={assetOptions || []} enableCurrencyIcon />
                            </PortalInputController>
                        </div>
                    </div>
                </fieldset>

                <fieldset className="space-y-3">
                    <PortalInputController
                        name="walletAddress"
                        label={lang.portal.walletAddress}
                        placeholder={lang.portal.walletAddress}>
                        <Input />
                    </PortalInputController>
                    <PortalInputController name="tagMemo" label={lang.portal.tagMemo} placeholder={lang.portal.tagMemo}>
                        <Input />
                    </PortalInputController>
                </fieldset>
                <PortalInputController
                    name="requestDescription"
                    label={lang.portal.requestDescription}
                    placeholder={lang.portal.requestDescription}>
                    <TextArea rows={2} />
                </PortalInputController>

                <fieldset className="space-y-3 mt-3">
                    <legend className="text-xs text-brand-neutral-500">{lang.portal.termsAndConditions}</legend>
                    <label className="flex cursor-pointer text-sm bg-background p-4 rounded-md border border-brand-neutral-700 gap-2 items-start">
                        <PortalInputCheckbox
                            name="walletTermsAndConditions"
                            className="!w-6 !h-6 !py-0"
                            title="Please check this box to initiate the wallet address whitelisting request"
                            checked={watch('walletTermsAndConditions') as any}
                            onChange={(e) => {
                                setValue('walletTermsAndConditions', e.target.checked, {
                                    shouldDirty: true,
                                    shouldValidate: true
                                });
                            }}
                        />
                        <div className="flex flex-col gap-2">
                            {/* TBA - Translations */}
                            <div className="text-sm">
                                I certify that the Digital Wallet Address (and if applicable Destination Tag) listed
                                above (collectively, the “Address”) is directly and wholly owned by the Counterparty
                                listed above, or to the extent it is associated with a Third Party, I acknowledge and
                                agree that:
                            </div>
                            <ol className="list-decimal pl-6 space-y-3 mt-3 text-sm ">
                                <li>
                                    the Third Party is designating the Address for sending and/or receiving digital
                                    assets to/from GCEX and;
                                </li>
                                <li>
                                    the digital assets that will be sent to or received from the Address are owned by
                                    the Counterparty.
                                </li>
                            </ol>
                        </div>
                    </label>
                    {errors['walletTermsAndConditions'] && (
                        <div className="flex justify-between items-center gap-3">
                            <div className="text-brand-red italic text-xs pt-1 w-full">
                                {errors['walletTermsAndConditions']?.message}
                            </div>
                        </div>
                    )}
                </fieldset>
            </Fragment>
        );
    }
};

export default RequestWalletInputs;
